/**
 * Agent-Chat Layout Component
 * Main application shell matching admin-ui sidebar pattern
 */

import React from 'react'
import { cn } from '@agion/ui'
import { Sidebar } from './Sidebar'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { useOnRevealEscalations } from '../../hooks/useEscalationsReveal'

interface LayoutProps {
  children: React.ReactNode
  header?: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children, header }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false)

  // Register global keyboard shortcuts
  useKeyboardShortcuts()

  // A question card's "Decide in Escalations" (agion/Agion#3446) expands the
  // sidebar that holds the section.
  const expandSidebar = React.useCallback(() => setSidebarCollapsed(false), [])
  useOnRevealEscalations(expandSidebar)

  return (
    <div className="flex h-screen bg-white dark:bg-slate-950">
      {/* Sidebar — premium dark mode */}
      <aside
        className={cn(
          "bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex-shrink-0 transition-all duration-300",
          sidebarCollapsed ? "w-16" : "w-80"
        )}
      >
        <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {header && (
          <header className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            {header}
          </header>
        )}

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  )
}

export default Layout
